import {
  useState,
  useMemo,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Plus,
  Search,
  Filter,
  Download,
  Loader2,
  List,
  FolderOpen,
  ArchiveRestore,
  FileSignature,
} from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { AgGridReact } from 'ag-grid-react'
import type { ColDef, GridSizeChangedEvent } from 'ag-grid-community'
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community'
import {
  useAssortmentPlans,
  useDeletePlan,
  useCopyPlan,
} from '@/hooks/useAssortmentPlans'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { useDebounce } from '@/hooks/useDebounce'
import { useExportPlans } from '@/hooks/useExportPlans'
import { useExportPlanDetails } from '@/hooks/useExportPlanDetails'
import {
  MAX_EXPORT_PLAN_IDS,
  type CreatePlanExportRequest,
} from '@/api/planExports'
import { OwnerFilterCombobox } from '@/components/common/OwnerFilterCombobox'
import { useSeasons } from '@/hooks/useSeasons'
import { mapStatusToApi } from '@/api/assortmentPlans'
import { Combobox } from '@/components/ui/combobox'
import { HierarchyFilter } from '@/components/AssortmentPlansGrid/HierarchyFilter'
import { PlanWizard } from '@/components/PlanWizard/PlanWizard'
import { BulkCommitmentLetterWizard } from '@/components/PlanDetail/CommitmentLetterWizard/BulkCommitmentLetterWizard'
import { MAX_BULK_PLANS } from '@/api/commitmentLetters'
import { SelectionBar } from '@/components/common/SelectionBar'
import { usePlanWizardStore } from '@/stores/usePlanWizardStore'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import { Pagination } from '@/components/common/Pagination'
import { usePersistedPageSize } from '@/hooks/usePersistedPageSize'
import { useElementHeight } from '@/hooks/useElementHeight'
import { TABLE_PAGE_SIZE_KEYS } from '@/stores/useTablePageSizeStore'
import {
  EditablePlanNameCell,
  ActiveVersionCell,
  TextCell,
  ActionsCell,
  StatusBadgeCell,
} from '@/components/AssortmentPlansGrid/cellRenderers'
import {
  PlanSelectionCell,
  PlanSelectionHeader,
} from '@/components/AssortmentPlansGrid/PlanSelection'
import { FolderBrowseView } from '@/components/FolderBrowse/FolderBrowseView'
import { useAssortmentPlanSelectionStore } from '@/stores/useAssortmentPlanSelectionStore'
import { useBrowseStore } from '@/stores/useBrowseStore'
import { cn } from '@/utils/cn'
import type { AssortmentPlan } from '@/types'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule])

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'Draft', label: 'Draft' },
  { value: 'Submitted', label: 'Submitted' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Sent Back', label: 'Sent Back' },
]

export default function AssortmentPlans() {
  const navigate = useNavigate()
  const [searchInput, setSearchInput] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [ownerFilter, setOwnerFilter] = useState<string | null>(null)
  const [hierarchyFilter, setHierarchyFilter] = useState<{
    departmentId: string | null
    classId: string | null
    subclassId: string | null
  }>({ departmentId: null, classId: null, subclassId: null })
  const [seasonFilter, setSeasonFilter] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = usePersistedPageSize(
    TABLE_PAGE_SIZE_KEYS.PLANS_LIST,
    20
  )
  const { openWizard } = usePlanWizardStore()
  const { viewMode, setViewMode } = useBrowseStore()
  const { ref: filterBarRef, height: filterBarHeight } =
    useElementHeight<HTMLDivElement>()

  // Seasons for season filter
  const { data: seasons, isLoading: seasonsLoading } = useSeasons()
  const seasonOptions = useMemo(
    () =>
      (seasons ?? []).map(s => ({
        value: s.id,
        label: s.name,
      })),
    [seasons]
  )

  // Compute the most specific hierarchy ID for filtering
  const hierarchyId =
    hierarchyFilter.subclassId ??
    hierarchyFilter.classId ??
    hierarchyFilter.departmentId

  // Modal state for actions
  const [copyDialogOpen, setCopyDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const [exportType, setExportType] = useState<'plans' | 'details'>('plans')
  const [selectedPlan, setSelectedPlan] = useState<AssortmentPlan | null>(null)

  // Mutations
  const deleteMutation = useDeletePlan()
  const copyMutation = useCopyPlan()

  // Export
  const { exportPlans, isProcessing: isExportingPlans } = useExportPlans()
  const { exportPlanDetails, isProcessing: isExportingDetails } =
    useExportPlanDetails()
  const isExporting = isExportingPlans || isExportingDetails

  // Plans checked for the SKU export (cleared when filters change)
  const { selectedPlanIds, clearSelection } = useAssortmentPlanSelectionStore()
  const selectedCount = selectedPlanIds.length
  const [commitmentLettersOpen, setCommitmentLettersOpen] = useState(false)
  const tooManyForLetters = selectedCount > MAX_BULK_PLANS
  useEffect(() => clearSelection, [clearSelection])

  const handleCopyClick = useCallback((plan: AssortmentPlan) => {
    setSelectedPlan(plan)
    setCopyDialogOpen(true)
  }, [])

  const handleDeleteClick = useCallback((plan: AssortmentPlan) => {
    setSelectedPlan(plan)
    setDeleteDialogOpen(true)
  }, [])

  const handleConfirmCopy = useCallback(() => {
    if (selectedPlan && selectedPlan.active_version_id) {
      copyMutation.mutate(
        {
          name: `${selectedPlan.name} (Copy)`,
          sourceVersionId: selectedPlan.active_version_id,
          hierarchyId: selectedPlan.hierarchy_id,
        },
        {
          onSettled: () => {
            setCopyDialogOpen(false)
            setSelectedPlan(null)
          },
        }
      )
    }
  }, [selectedPlan, copyMutation])

  const handleConfirmDelete = useCallback(() => {
    if (selectedPlan) {
      deleteMutation.mutate(selectedPlan.id, {
        onSettled: () => {
          setDeleteDialogOpen(false)
          setSelectedPlan(null)
        },
      })
    }
  }, [selectedPlan, deleteMutation])

  // Debounce search input to avoid excessive API calls
  const debouncedSearch = useDebounce(searchInput, 300)

  // Filters shared by the list query, select-all and the export
  const listFilters = useMemo(
    () => ({
      search: debouncedSearch,
      status: statusFilter,
      owner_id: ownerFilter ?? undefined,
      hierarchy_id: hierarchyId ?? undefined,
      season_id: seasonFilter ?? undefined,
    }),
    [debouncedSearch, statusFilter, ownerFilter, hierarchyId, seasonFilter]
  )

  const { data, isLoading } = useAssortmentPlans({
    ...listFilters,
    page,
    pageSize,
  })

  const plans = data?.plans ?? []
  const totalCount = data?.totalCount ?? 0
  const totalPages = data?.totalPages ?? 1

  // Column definitions for AG Grid - responsive with flex sizing
  const columnDefs: ColDef<AssortmentPlan>[] = useMemo(
    () => [
      {
        colId: 'selection',
        headerName: '',
        width: 52,
        minWidth: 52,
        maxWidth: 52,
        pinned: 'left',
        sortable: false,
        resizable: false,
        headerComponent: PlanSelectionHeader,
        headerComponentParams: { totalCount, filters: listFilters },
        cellRenderer: PlanSelectionCell,
      },
      {
        field: 'name',
        headerName: 'Plan Name',
        flex: 2,
        minWidth: 180,
        cellRenderer: EditablePlanNameCell,
      },
      {
        field: 'hierarchy',
        headerName: 'Hierarchy',
        flex: 3,
        minWidth: 200,
        cellRenderer: TextCell,
      },
      {
        field: 'versions_count',
        headerName: 'Version',
        flex: 1,
        minWidth: 100,
        maxWidth: 200,
        cellRenderer: ActiveVersionCell,
      },
      {
        field: 'owner',
        headerName: 'Owner',
        flex: 1,
        minWidth: 100,
        cellRenderer: TextCell,
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        minWidth: 100,
        maxWidth: 130,
        cellRenderer: StatusBadgeCell,
      },
      {
        headerName: 'Actions',
        flex: 1,
        minWidth: 90,
        maxWidth: 120,
        cellClass: 'ag-right-aligned-cell',
        headerClass: 'ag-right-aligned-header',
        cellRenderer: ActionsCell,
        cellRendererParams: {
          onCopy: handleCopyClick,
          onDelete: handleDeleteClick,
        },
      },
    ],
    [handleCopyClick, handleDeleteClick, totalCount, listFilters]
  )

  // Handle dynamic resizing when container size changes
  const onGridSizeChanged = useCallback((params: GridSizeChangedEvent) => {
    setTimeout(() => {
      params.api.sizeColumnsToFit()
    }, 10)
  }, [])

  // Export handler. Must mirror listFilters so the export covers exactly the listed plans.
  const exportFilters = useMemo<CreatePlanExportRequest>(() => {
    const status = mapStatusToApi(statusFilter)
    return {
      search: debouncedSearch.trim() || undefined,
      status: status ? [status] : undefined,
      owner_ids: ownerFilter ? [ownerFilter] : undefined,
      hierarchy_ids: hierarchyId ? [hierarchyId] : undefined,
      season_ids: seasonFilter ? [seasonFilter] : undefined,
    }
  }, [debouncedSearch, statusFilter, ownerFilter, hierarchyId, seasonFilter])

  const handleExport = useCallback(() => {
    // Checked plans are for the SKU export, so preselect it
    if (selectedCount > 0) setExportType('details')
    setExportDialogOpen(true)
  }, [selectedCount])

  // Selecting every filtered plan is the same as exporting by filters; sending the
  // filters instead of the ids keeps large selections under the id cap.
  const allSelected = selectedCount > 0 && selectedCount >= totalCount
  const sendPlanIds = selectedCount > 0 && !allSelected
  const tooManyForExport = sendPlanIds && selectedCount > MAX_EXPORT_PLAN_IDS
  const tooManyForExportMessage = `An export can list at most ${MAX_EXPORT_PLAN_IDS.toLocaleString()} selected plans. Select all filtered plans or narrow the filters instead.`
  const tooManySelected = exportType === 'details' && tooManyForExport

  const exportSelectedDetails = useCallback(() => {
    exportPlanDetails(
      sendPlanIds
        ? { ...exportFilters, plan_ids: selectedPlanIds }
        : exportFilters
    )
  }, [exportPlanDetails, exportFilters, sendPlanIds, selectedPlanIds])

  const handleConfirmExport = useCallback(() => {
    if (exportType === 'details') {
      exportSelectedDetails()
    } else {
      exportPlans(exportFilters)
    }
    setExportDialogOpen(false)
  }, [exportType, exportSelectedDetails, exportPlans, exportFilters])

  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(1)
  }, [
    debouncedSearch,
    statusFilter,
    ownerFilter,
    hierarchyId,
    seasonFilter,
    pageSize,
  ])

  // A selection only makes sense for the list it was made from
  useEffect(() => {
    clearSelection()
  }, [listFilters, clearSelection])

  return (
    <>
      <PlanWizard />

      <BulkCommitmentLetterWizard
        open={commitmentLettersOpen}
        onOpenChange={setCommitmentLettersOpen}
        planIds={selectedPlanIds}
      />

      {/* Copy Confirmation Dialog */}
      <ConfirmationDialog
        open={copyDialogOpen}
        onOpenChange={setCopyDialogOpen}
        title="Copy plan"
        description={
          selectedPlan?.active_version_id
            ? `Are you sure you want to copy "${selectedPlan?.name ?? ''}"? A duplicate will be created.`
            : `Cannot copy "${selectedPlan?.name ?? ''}" because it has no active version.`
        }
        confirmText="Copy"
        cancelText="Cancel"
        onConfirm={handleConfirmCopy}
        isLoading={copyMutation.isPending}
        confirmDisabled={!selectedPlan?.active_version_id}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete plan"
        description={`Are you sure you want to delete "${selectedPlan?.name ?? ''}"?`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        variant="destructive"
        isLoading={deleteMutation.isPending}
      />

      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Export assortment plans</DialogTitle>
            <DialogDescription>Choose what to export</DialogDescription>
          </DialogHeader>
          <RadioGroup
            value={exportType}
            onValueChange={value => setExportType(value as 'plans' | 'details')}
            className="gap-4"
          >
            <div className="flex items-start gap-3">
              <RadioGroupItem value="plans" id="export-plans" />
              <div className="grid gap-1">
                <Label htmlFor="export-plans">Export list of plans</Label>
                <p className="text-sm text-muted-foreground">
                  Export the entire plan list with the current filters.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <RadioGroupItem value="details" id="export-plan-details" />
              <div className="grid gap-1">
                <Label htmlFor="export-plan-details">
                  Export plan details (SKU level)
                </Label>
                <p className="text-sm text-muted-foreground">
                  {selectedCount > 0
                    ? `SKU details for the ${selectedCount.toLocaleString()} selected ${selectedCount === 1 ? 'plan' : 'plans'}.`
                    : `SKU details for all ${totalCount.toLocaleString()} ${totalCount === 1 ? 'plan' : 'plans'} matching your current filters. Select plans in the list to export only those.`}{' '}
                  Plans without SKUs add no rows.
                </p>
                {tooManySelected && (
                  <p className="text-sm text-destructive">
                    {tooManyForExportMessage}
                  </p>
                )}
              </div>
            </div>
          </RadioGroup>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setExportDialogOpen(false)}
              disabled={isExporting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmExport}
              disabled={isExporting || tooManySelected}
            >
              {isExporting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              Export
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div
        className="flex flex-col gap-6 p-6"
        style={
          {
            '--sticky-toolbar-offset': `${filterBarHeight}px`,
          } as React.CSSProperties
        }
      >
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              Assortment Plans
            </h1>
            <p className="text-base text-foreground mt-1">
              Manage and create category assortment plans
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center rounded-md border border-border">
              <button
                type="button"
                aria-label="Folder view"
                aria-pressed={viewMode === 'folder'}
                onClick={() => setViewMode('folder')}
                className={cn(
                  'p-2 rounded-l-md cursor-pointer transition-colors',
                  viewMode === 'folder'
                    ? 'bg-[#FEF2F3] text-[#CF1F2E]'
                    : 'text-muted-foreground hover:bg-muted/50'
                )}
              >
                <FolderOpen className="h-4 w-4" />
              </button>
              <button
                type="button"
                aria-label="List view"
                aria-pressed={viewMode === 'list'}
                onClick={() => setViewMode('list')}
                className={cn(
                  'p-2 rounded-r-md cursor-pointer transition-colors',
                  viewMode === 'list'
                    ? 'bg-[#FEF2F3] text-[#CF1F2E]'
                    : 'text-muted-foreground hover:bg-muted/50'
                )}
              >
                <List className="h-4 w-4" />
              </button>
            </div>
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label="Deleted plans"
                    onClick={() => navigate('/plans/deleted')}
                  >
                    <ArchiveRestore className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Deleted plans</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button className="gap-2" onClick={openWizard}>
              <Plus className="h-4 w-4" />
              Create New Plan
            </Button>
          </div>
        </div>

        {viewMode === 'list' ? (
          <>
            {/* Filter Bar */}
            <div
              ref={filterBarRef}
              className="sticky top-0 z-20 flex items-center gap-3 rounded-lg border border-border bg-card p-3"
            >
              {/* Search Input */}
              <div className="relative flex-1 max-w-[352px]">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search plans..."
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  className="pl-9 bg-muted/50"
                />
              </div>

              {/* Filter Icon */}
              <Filter className="h-6 w-6 text-muted-foreground" />

              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Owner Filter */}
              <OwnerFilterCombobox
                value={ownerFilter}
                onValueChange={setOwnerFilter}
              />

              {/* Hierarchy Filter */}
              <HierarchyFilter
                value={hierarchyFilter}
                onValueChange={setHierarchyFilter}
              />

              {/* Season Filter */}
              <Combobox
                options={seasonOptions}
                value={seasonFilter}
                onValueChange={setSeasonFilter}
                placeholder="All Seasons"
                isLoading={seasonsLoading}
                className="w-[180px]"
              />

              {/* Separator */}
              <Separator orientation="vertical" className="h-10" />

              <Button
                variant="outline"
                className="gap-2"
                onClick={handleExport}
                disabled={isExporting}
              >
                {isExporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Export
              </Button>
            </div>

            {/* AG Grid Table */}
            <div className="w-full">
              {selectedCount > 0 && (
                <SelectionBar
                  count={selectedCount}
                  noun="Plan"
                  onClear={clearSelection}
                >
                  <SelectionActionButton
                    icon={<FileSignature className="h-4 w-4" />}
                    label="Create Commitment Letter"
                    disabledReason={
                      tooManyForLetters
                        ? `Select up to ${MAX_BULK_PLANS} plans to create commitment letters`
                        : null
                    }
                    onClick={() => setCommitmentLettersOpen(true)}
                  />
                  <SelectionActionButton
                    icon={
                      isExporting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4" />
                      )
                    }
                    label="Export Plan Details (SKU level)"
                    disabledReason={
                      tooManyForExport ? tooManyForExportMessage : null
                    }
                    disabled={isExporting}
                    onClick={exportSelectedDetails}
                  />
                </SelectionBar>
              )}
              <div className="border border-[#e7e7e7] bg-white">
                <div className="ag-theme-michaels plans-list-grid w-full">
                  <AgGridReact<AssortmentPlan>
                    rowData={plans}
                    columnDefs={columnDefs}
                    theme="legacy"
                    loading={isLoading}
                    domLayout="autoHeight"
                    rowHeight={68}
                    headerHeight={48}
                    autoSizeStrategy={{ type: 'fitGridWidth' }}
                    onGridSizeChanged={onGridSizeChanged}
                    overlayLoadingTemplate="<span>Loading...</span>"
                    overlayNoRowsTemplate="<span>No plans found</span>"
                    suppressCellFocus={true}
                    suppressRowHoverHighlight={false}
                    suppressColumnVirtualisation={true}
                    suppressMovableColumns={true}
                  />
                </div>

                {/* Pagination */}
                <Pagination
                  page={page}
                  pageSize={pageSize}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                />
              </div>
            </div>
          </>
        ) : (
          <FolderBrowseView />
        )}
      </div>
    </>
  )
}

interface SelectionActionButtonProps {
  icon: ReactNode
  label: string
  /** Tooltip explaining why the button is disabled; null when enabled. */
  disabledReason: string | null
  /** Disables the button without a tooltip, e.g. while a job is running. */
  disabled?: boolean
  onClick: () => void
}

/** Bulk action shown in the selection bar on top of the plans table. */
function SelectionActionButton({
  icon,
  label,
  disabledReason,
  disabled,
  onClick,
}: SelectionActionButtonProps) {
  const button = (
    <Button
      variant="outline"
      className="gap-2 border-primary text-primary hover:bg-primary/10"
      onClick={onClick}
      disabled={disabled || !!disabledReason}
    >
      {icon}
      {label}
    </Button>
  )
  if (!disabledReason) return button

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* span keeps the tooltip working on a disabled button */}
          <span>{button}</span>
        </TooltipTrigger>
        <TooltipContent>{disabledReason}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
